import type { EventModuleKind, EventModulePage, EventPassportReturnPage } from './const'
import { buildProgramItemRegulationModuleSlug } from '~/entities/round-table/program/model/programItemRegulationSlug'
import { permissions } from '~/shared/lib'

export type EventProgramReturnPage = 'edit' | 'view'

interface EventModuleRoute {
  name: string
  params: { id: string }
  query?: Record<string, string>
}

export function resolvePassportReturnPageFromQuery(
  query: Record<string, unknown>,
): EventPassportReturnPage {
  return query.passportReturnPage === 'view' ? 'view' : 'edit'
}

export function buildLinkedEventModuleEditQuery(options: {
  passportModuleSlug: string
  passportReturnPage?: EventPassportReturnPage
}): Record<string, string> {
  return {
    passportModuleSlug: options.passportModuleSlug,
    passportReturnPage: options.passportReturnPage ?? 'edit',
  }
}

export function resolveProgramReturnPageFromQuery(
  query: Record<string, unknown>,
): EventProgramReturnPage {
  return query.programReturnPage === 'view' ? 'view' : 'edit'
}

export function buildLinkedProgramItemRegulationEditQuery(options: {
  programModuleSlug: string
  programItemId: string
  programReturnPage?: EventProgramReturnPage
  passportModuleSlug?: string
  passportReturnPage?: EventPassportReturnPage
}): Record<string, string> {
  return {
    moduleSlug: buildProgramItemRegulationModuleSlug(
      options.programModuleSlug,
      options.programItemId,
    ),
    programModuleSlug: options.programModuleSlug,
    programItemId: options.programItemId,
    programReturnPage: options.programReturnPage ?? 'edit',
    ...(options.passportModuleSlug
      ? {
          passportModuleSlug: options.passportModuleSlug,
          passportReturnPage: options.passportReturnPage ?? 'edit',
        }
      : {}),
  }
}

export function resolveEventModuleRoute(
  kind: EventModuleKind,
  projectId: string,
  moduleSlug: string,
  page: EventModulePage,
): EventModuleRoute {
  const params = { id: projectId }
  const query = moduleSlug ? { moduleSlug } : undefined
  const workspace = permissions.PROJECTS.WORKSPACE

  switch (kind) {
    case 'passport':
      if (page === 'entry') {
        return { name: workspace.EVENT_PASSPORT.PERMISSION, params, query }
      }
      if (page === 'edit') {
        return { name: workspace.EVENT_PASSPORT.EDIT.PERMISSION, params, query }
      }
      return { name: workspace.EVENT_PASSPORT.VIEW.PERMISSION, params, query }
    case 'program':
      if (page === 'entry') {
        return { name: workspace.EVENT_PROGRAM.PERMISSION, params, query }
      }
      if (page === 'edit') {
        return { name: workspace.EVENT_PROGRAM.EDIT.PERMISSION, params, query }
      }
      return { name: workspace.EVENT_PROGRAM.VIEW.PERMISSION, params, query }
    case 'regulation':
      if (page === 'entry') {
        return { name: workspace.EVENT_REGULATION.PERMISSION, params, query }
      }
      if (page === 'edit') {
        return { name: workspace.EVENT_REGULATION.EDIT.PERMISSION, params, query }
      }
      return { name: workspace.EVENT_REGULATION.VIEW.PERMISSION, params, query }
  }
}

export function resolveEventPassportParticipantsRoute(
  projectRouteParam: string,
  moduleSlug: string,
  nav?: 'participants' | 'applications',
): EventModuleRoute {
  return {
    name: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.PARTICIPANTS.PERMISSION,
    params: { id: projectRouteParam },
    query: {
      moduleSlug,
      ...(nav ? { nav } : {}),
    },
  }
}

export function resolveEventApplicationApprovalRoute(
  projectRouteParam: string,
  moduleSlug: string,
  applicationId: string,
): EventModuleRoute {
  return {
    name: permissions.PROJECTS.WORKSPACE.EVENT_APPLICATION.APPROVAL.PERMISSION,
    params: {
      id: projectRouteParam,
      applicationId,
    },
    query: { moduleSlug },
  }
}

export function navigateToEventPassportParticipantsRoute(
  projectRouteParam: string,
  moduleSlug: string,
  options?: {
    replace?: boolean
    nav?: 'participants' | 'applications'
  },
) {
  void navigateTo(
    resolveEventPassportParticipantsRoute(projectRouteParam, moduleSlug, options?.nav),
    { replace: options?.replace ?? false },
  )
}

export function navigateToEventApplicationApprovalRoute(
  projectRouteParam: string,
  moduleSlug: string,
  applicationId: string,
  options?: { replace?: boolean },
) {
  void navigateTo(
    resolveEventApplicationApprovalRoute(projectRouteParam, moduleSlug, applicationId),
    { replace: options?.replace ?? false },
  )
}

export function resolveEventApplicationRoute(
  projectId: string,
  moduleSlug: string,
): EventModuleRoute {
  return {
    name: permissions.PROJECTS.WORKSPACE.EVENT_APPLICATION.PERMISSION,
    params: { id: projectId },
    query: { moduleSlug },
  }
}

export function navigateToEventModuleRoute(
  kind: EventModuleKind,
  projectId: string,
  moduleSlug: string,
  page: EventModulePage,
  options?: {
    replace?: boolean
    query?: Record<string, string>
  },
) {
  const route = resolveEventModuleRoute(kind, projectId, moduleSlug, page)
  void navigateTo({
    ...route,
    query: {
      ...(route.query ?? {}),
      ...options?.query,
    },
  }, {
    replace: options?.replace ?? false,
  })
}

export function navigateToEventApplicationRoute(
  projectId: string,
  moduleSlug: string,
  options?: { replace?: boolean },
) {
  void navigateTo(resolveEventApplicationRoute(projectId, moduleSlug), {
    replace: options?.replace ?? false,
  })
}
